import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { MessageSquare, Send, RefreshCw, Phone as PhoneIcon, ExternalLink, ArrowLeft, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useData } from "../contexts/DataContext";
import { InboxListSkeleton } from "../components/skeletons";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function fmtLong(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

export default function Inbox() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null); // phone_e164
  const [thread, setThread] = useState({ messages: [], phone_e164: "", twilio_number: "" });
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [twilioConfigured, setTwilioConfigured] = useState(true);
  const [templates, setTemplates] = useState([]);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Per-conversation draft persistence — survives refresh/navigation so an
  // incoming text mid-reply doesn't nuke a half-written response.
  const draftKey = (phone) => `inbox-draft:${phone}`;
  const loadDraft = (phone) => {
    try { return phone ? (localStorage.getItem(draftKey(phone)) || "") : ""; }
    catch { return ""; }
  };
  const persistDraft = (phone, value) => {
    if (!phone) return;
    try {
      if (value) localStorage.setItem(draftKey(phone), value);
      else localStorage.removeItem(draftKey(phone));
    } catch { /* private-mode etc — ignore */ }
  };

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const loadConversations = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/sms/conversations`, { headers: auth() });
      setConversations(data.conversations || []);
      return true;
    } catch (err) {
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const loadThread = async (phone) => {
    if (!phone) return;
    try {
      const { data } = await axios.get(`${API_URL}/sms/conversations/${encodeURIComponent(phone)}`, { headers: auth() });
      setThread(data);
      await axios.post(`${API_URL}/sms/conversations/${encodeURIComponent(phone)}/mark-read`, {}, { headers: auth() });
      loadConversations();  // refresh unread counts
      // Immediately sync the native app-icon badge to the new unread total
      // so the user doesn't have to wait for the next 30s poll.
      try {
        const { refreshAppBadge } = await import("../lib/badge");
        await refreshAppBadge();
      } catch { /* not native — silent */ }
    } catch (err) {
      toast.error("Couldn't load conversation");
    }
  };

  const checkTwilio = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/sms`, { headers: auth() });
      setTwilioConfigured(!!data.twilio_configured);
    } catch { /* ignore */ }
  };

  const loadTemplates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/sms/templates`, { headers: auth() });
      setTemplates(data.templates || []);
    } catch { /* ignore — chip row simply won't render */ }
  };

  const applyTemplate = (tpl) => {
    // Fill the composer with the template body. Doesn't auto-send — the user
    // reviews and taps Send.
    setDraft((prev) => {
      const trimmed = prev.trim();
      // Replace only when the composer is empty; otherwise append with a space.
      return trimmed ? `${prev} ${tpl.body}` : tpl.body;
    });
    // Focus the textarea so the user can tweak/send right away.
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  useEffect(() => { loadConversations(); checkTwilio(); loadTemplates();
    // Reading the Inbox is the point where "you've caught up" — kill the
    // home-screen badge immediately so the "1" doesn't linger after this
    // screen mounts.
    import("../lib/badge").then((m) => m.clearAppBadge?.()).catch(() => {});
  }, []);

  // Auto-refresh the inbox every 30 seconds. Refreshes both the
  // conversation list AND the currently-open thread so new messages
  // show up without the user pulling to refresh. Skips when the tab
  // is hidden to save battery on mobile.
  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      loadConversations();
      if (selected) loadThread(selected);
    };
    const interval = setInterval(tick, 30_000);
    // Also refresh the moment the user comes back to the tab.
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);
  // Deep-link support: /inbox?phone=+18435551234 (used by push notifications)
  useEffect(() => {
    const phone = searchParams.get("phone");
    if (phone) setSelected(phone);
  }, [searchParams]);
  useEffect(() => {
    if (selected) loadThread(selected);
    // Restore any draft the user had for this conversation
    setDraft(loadDraft(selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [thread.messages]);

  // Persist the draft for the currently-open conversation on every keystroke.
  useEffect(() => {
    if (selected) persistDraft(selected, draft);
  }, [draft, selected]);

  const send = async () => {
    const body = draft.trim();
    if (!body || !selected) return;
    setSending(true);
    try {
      const result = await axios.post(
        `${API_URL}/sms/conversations/${encodeURIComponent(selected)}/send`,
        { body },
        { headers: auth() },
      );
      if (result.data?.ok) {
        toast.success("Sent");
        setDraft("");
        persistDraft(selected, "");  // clear the stored draft for this thread
        loadThread(selected);
      } else {
        toast.error(result.data?.reason || "Send failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const currentConvo = conversations.find((c) => c.phone_e164 === selected);

  // Fallback contact resolver: if the backend hasn't linked this
  // conversation to a contact yet (freshly-opened deep link, or the
  // contact's phone is stored in a messy format), search the frontend
  // DataContext by digits-only match so the header still shows a name
  // instead of raw digits.
  const { contacts: allContacts } = useData();
  const digitsOnly = (v) => (v || "").replace(/\D/g, "");
  const displayNameFor = (c) => {
    if (!c) return "";
    const full = (c.name || "").trim();
    const built = `${c.first_name || ""} ${c.last_name || ""}`.trim();
    return built || full;
  };
  const selectedDigits = digitsOnly(selected);
  const fallbackContact = !currentConvo?.contact_name && selectedDigits
    ? allContacts?.find((c) => {
        const a = digitsOnly(c.sms_phone_e164);
        const b = digitsOnly(c.phone);
        return [a, b].some((d) => {
          if (!d) return false;
          if (d === selectedDigits) return true;
          if (d.length === 11 && d.startsWith("1") && d.slice(1) === selectedDigits.replace(/^1/, "")) return true;
          if (d.length === 10 && selectedDigits.replace(/^1/, "") === d) return true;
          return false;
        });
      })
    : null;
  const displayName = currentConvo?.contact_name || displayNameFor(fallbackContact) || selected;
  const displayContactId = currentConvo?.contact_id || fallbackContact?.id || "";

  return (
    <div className="h-[calc(100%+6rem)] -mb-24 lg:h-[calc(100vh-3.5rem)] lg:mb-0 flex flex-col" data-testid="inbox-page">
      <div className="border-b border-slate-200 bg-white px-6 py-3 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Inbox
        </h1>
        <div className="flex items-center gap-3">
          {!twilioConfigured && (
            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-1">
              Twilio not configured
            </span>
          )}
          <Button variant="outline" size="sm" disabled={refreshing} onClick={async () => { setRefreshing(true); try { const ok = await loadConversations(); if (selected) await loadThread(selected); if (ok) toast.success("Inbox up to date"); else toast.error("Could not refresh. Check your connection."); } finally { setRefreshing(false); } }} data-testid="inbox-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Refreshing" : "Refresh"}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: conversation list.
            On mobile it takes the full width when no conversation is
            selected, and hides completely once one is open so the thread
            gets the whole screen. Desktop keeps the split view. */}
        <div
          className={`${selected ? "hidden sm:block" : "block"} w-full sm:w-72 border-r border-slate-200 overflow-y-auto bg-slate-50`}
          data-testid="inbox-convo-list"
        >
          {loading ? (
            <InboxListSkeleton rows={6} />
          ) : conversations.length === 0 ? (
            <div className="p-6 text-sm text-slate-400 text-center">
              <MessageSquare className="w-6 h-6 mx-auto mb-2 text-slate-300" />
              No conversations yet
            </div>
          ) : (
            conversations.map((c) => {
              const isSelected = c.phone_e164 === selected;
              // Same fallback logic as the header: if the backend
              // didn't link a name (returns the phone as contact_name),
              // resolve from the frontend contacts list by digits-only.
              const rawName = c.contact_name || "";
              const looksLikePhone = rawName.replace(/\D/g, "").length >= 10 && !/[a-zA-Z]/.test(rawName);
              let listName = rawName;
              if (looksLikePhone && allContacts?.length) {
                const cd = digitsOnly(c.phone_e164);
                const match = allContacts.find((ct) => {
                  const a = digitsOnly(ct.sms_phone_e164);
                  const b = digitsOnly(ct.phone);
                  return [a, b].some((d) => {
                    if (!d) return false;
                    if (d === cd) return true;
                    if (d.length === 11 && d.startsWith("1") && d.slice(1) === cd.replace(/^1/, "")) return true;
                    if (d.length === 10 && ("1" + d) === cd) return true;
                    return false;
                  });
                });
                if (match) listName = displayNameFor(match) || match.name;
              }
              return (
                <button
                  key={c.phone_e164}
                  onClick={() => setSelected(c.phone_e164)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 transition ${
                    isSelected ? "bg-white shadow-sm" : "hover:bg-white"
                  }`}
                  data-testid={`inbox-convo-${c.phone_e164}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-800 truncate">{listName}</span>
                    <span className="text-[11px] text-slate-400 flex-shrink-0">{fmtTime(c.last_message_at)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <p className="text-xs text-slate-500 truncate flex-1">
                      {c.last_direction === "outbound" ? "You: " : ""}{c.last_message_body}
                    </p>
                    {c.unread_count > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none flex-shrink-0">
                        {c.unread_count > 99 ? "99+" : c.unread_count}
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Right: thread. Full-screen on mobile when open. */}
        <div className={`${selected ? "flex" : "hidden sm:flex"} flex-1 flex-col bg-white`}>
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400" data-testid="inbox-empty-state">
              Pick a conversation to view messages
            </div>
          ) : (
            <>
              <div className="border-b border-slate-200 px-4 sm:px-5 py-3 flex items-center justify-between gap-2">
                <button
                  onClick={() => setSelected(null)}
                  className="sm:hidden -ml-1 h-10 w-10 rounded-lg flex items-center justify-center text-slate-600 active:bg-slate-100"
                  aria-label="Back to conversations"
                  data-testid="inbox-back-btn"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-slate-800 truncate">{displayName}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-2 truncate">
                    <PhoneIcon className="w-3 h-3 flex-shrink-0" /> {selected}
                    {thread.twilio_number && <span className="text-slate-400 hidden sm:inline">· From {thread.twilio_number}</span>}
                  </p>
                </div>
                {displayContactId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/contacts/${displayContactId}`)}
                    data-testid="inbox-open-contact-btn"
                    className="flex-shrink-0"
                  >
                    <ExternalLink className="w-3.5 h-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Open contact</span>
                  </Button>
                )}
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-6 bg-slate-50" data-testid="inbox-thread">
                <div className="space-y-2">
                  {thread.messages.map((m) => {
                    const outbound = m.direction === "outbound";
                    return (
                      <div key={m.id} className={`flex ${outbound ? "justify-end" : "justify-start"}`} data-testid={`inbox-msg-${m.id}`}>
                        <div className={`max-w-[70%] rounded-2xl px-4 py-2 shadow-sm ${
                          outbound ? "bg-[#0F4C81] text-white" : "bg-white text-slate-800 border border-slate-200"
                        }`}>
                          <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                          <p className={`text-[10px] mt-1 ${outbound ? "text-blue-200" : "text-slate-400"}`}>
                            {fmtLong(m.created_at)}
                            {m.status && m.status !== "received" && ` · ${m.status}`}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div
                className="border-t border-slate-200 bg-white"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
              >
                {/* Quick-reply chip row — one-tap templates that fill the
                    composer (never auto-send). Scrolls horizontally when
                    the chips overflow on narrow screens. */}
                {templates.length > 0 && (
                  <div
                    className="px-3 sm:px-5 pt-2.5 pb-1 flex items-center gap-2 overflow-x-auto no-scrollbar"
                    data-testid="inbox-template-chips"
                  >
                    <Zap className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" aria-hidden="true" />
                    {templates.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => applyTemplate(t)}
                        className="flex-shrink-0 h-8 px-3 rounded-full bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-xs font-medium text-slate-700 border border-slate-200 transition-colors"
                        data-testid={`inbox-template-chip-${t.id}`}
                        title={t.body}
                      >
                        {t.title}
                      </button>
                    ))}
                  </div>
                )}
                <div className="px-3 sm:px-5 py-2.5">
                  <div className="flex items-end gap-2">
                    <textarea
                      ref={textareaRef}
                      className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-base sm:text-sm min-h-[44px] max-h-32 resize-none focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/40"
                      placeholder="Type a reply…"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          send();
                        }
                      }}
                      disabled={sending}
                      data-testid="inbox-reply-input"
                    />
                    <Button
                      onClick={send}
                      disabled={sending || !draft.trim()}
                      className="bg-[#0F4C81] hover:bg-[#0F4C81]/90 flex-shrink-0 h-11 w-11 p-0"
                      data-testid="inbox-send-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="hidden sm:block text-[11px] text-slate-400 mt-1.5">
                    Sends from {thread.twilio_number || "your Twilio number"}. Press Enter to send, Shift+Enter for a new line.
                    {draft.trim() && <span className="ml-2 text-emerald-600" data-testid="inbox-draft-saved">· Draft saved</span>}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
